import { useStreamPublisherStore } from '@features/streaming';
import { getConnectionStatusLabel } from '@entities/stream';
import {
  Panel,
  StatusBadge,
  BroadcastIcon,
  CameraIcon,
  MicIcon,
  WifiIcon,
  MonitorIcon,
  VideoCameraIcon,
  ClockIcon,
  GaugeIcon,
} from '@shared/ui';

/**
 * Stream status overview shown at the bottom of the Preview page.
 *
 * Camera / mic "connected" badges follow the publisher lifecycle: the
 * streaming feature owns publishing, so the status bar reads from the
 * same store rather than mirroring the room connection (the two can
 * legitimately diverge — e.g. a connected room with no published tracks
 * is "camera off" from the streaming POV).
 */
export function StreamStatusBar() {
  const connection = useStreamPublisherStore((state) => state.connection);
  const metrics = useStreamPublisherStore((state) => state.metrics);
  const virtualCamera = useStreamPublisherStore((state) => state.virtualCamera);

  return (
    <Panel
      title="STREAM STATUS"
      titleIcon={<BroadcastIcon className="h-4 w-4 text-accent" />}
      className="!p-4"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 xl:grid-cols-7">
        <StatusBadge
          icon={<CameraIcon className="h-5 w-5" />}
          label="Camera"
          value={getConnectionStatusLabel(connection.cameraConnected)}
          tone={connection.cameraConnected ? 'success' : 'muted'}
        />
        <StatusBadge
          icon={<MicIcon className="h-5 w-5" />}
          label="Microphone"
          value={getConnectionStatusLabel(connection.microphoneConnected)}
          tone={connection.microphoneConnected ? 'success' : 'muted'}
        />
        <StatusBadge
          icon={<WifiIcon className="h-5 w-5" />}
          label="Stream"
          value={connection.streamConnected ? 'Live' : 'Offline'}
          tone={connection.streamConnected ? 'live' : 'muted'}
        />
        <StatusBadge
          icon={<MonitorIcon className="h-5 w-5" />}
          label="OBS"
          value={getConnectionStatusLabel(connection.obsConnected)}
          tone={connection.obsConnected ? 'success' : 'muted'}
        />
        <StatusBadge
          icon={<VideoCameraIcon className="h-5 w-5" />}
          label="Virtual Camera"
          value={virtualCamera === 'enabled' ? 'Enabled' : 'Disabled'}
          tone={virtualCamera === 'enabled' ? 'success' : 'muted'}
        />
        <StatusBadge
          icon={<ClockIcon className="h-5 w-5" />}
          label="Latency"
          value={`${metrics.latencyMs} ms`}
          tone="success"
        />
        <StatusBadge
          icon={<GaugeIcon className="h-5 w-5" />}
          label="FPS"
          value={String(metrics.fps)}
          tone="success"
        />
      </div>
    </Panel>
  );
}
